import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Masked } from "work/masked-fields";
import { Field, FieldTitle, FieldValue } from "work/work-agreements/_views/view/tabs/work-agreement-details-field";
import WorkAgreementRevisionLinks from "work/work-agreements/_views/view/work-agreement-revision-links";
import WorkAgreement from "work/work-agreements/work-agreement";
import { workPaneProseInset } from "work/_views/work-pane-layout";

/**
 * The fields, on the same left edge as the actions row above them — the prose inset rather than a
 * margin of the tab's own, which put them 24px right of the Leave Team button (and the values 8px
 * further still). See `workPaneProseInset`.
 */
const WorkAgreementContainer = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  height: "100%",
  marginTop: theme.spacing(3),
}));
/** What the Client Representative field says when the client leader's name cannot be shown. */
export const CLIENT_REPRESENTATIVE_UNAVAILABLE = "Client representative not available";

type DetailsTabProps = {
  workAgreement: WorkAgreement;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const { workAgreement } = props;

  const individualUserIds = useMemo(() => {
    const ids: Guid[] = [];
    if (Masked.isPresentNotMasked(workAgreement.clientTeam) && workAgreement.clientTeam.leader?.userId) {
      ids.push(workAgreement.clientTeam.leader.userId);
    }
    return ids;
  }, [workAgreement.clientTeam]);

  const individualsByUserIds = useSelector(getIndividualsByUserIds(individualUserIds));

  const representativeLookup = useGetIndividualsByUserIdsQuery(
    { userIds: individualUserIds.map(id => id.value) },
    { skip: !individualUserIds.length }
  );

  const representative = individualsByUserIds?.find((individual) =>
    !Masked.isMasked(workAgreement.clientTeam) &&
    individual.userId.isEqualTo(workAgreement.clientTeam?.leader?.userId ?? Guid.EMPTY),
  );
  // The name arrives in a second request after the agreement, and the field read blank both while
  // it was on its way and after it failed — a blank that looks like an agreement with no client.
  let clientName: string;
  if (Masked.isMasked(workAgreement.clientTeam)) clientName = "Insufficient Permissions to View Client";
  else if (representative) clientName = representative.fullName;
  else if (representativeLookup.isFetching) clientName = "Loading...";
  else if (representativeLookup.isError) clientName = "Could not load the client representative";
  // Answered, without them: the server would not disclose the profile to this reader, or the
  // agreement names no client leader. Either way a blank would read as no client at all (#6094).
  else clientName = CLIENT_REPRESENTATIVE_UNAVAILABLE;

  return (
    <WorkAgreementContainer>
      <Field>
        <FieldTitle>Client Representative</FieldTitle>
        <FieldValue>{clientName}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>Description</FieldTitle>
        <FieldValue>{workAgreement.description?.value}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>Start Date</FieldTitle>
        <FieldValue>{workAgreement.startDate?.asFormattedString  ?? "None Specified"}</FieldValue>
      </Field>
      <Field>
        <FieldTitle>End Date</FieldTitle>
        <FieldValue>{workAgreement.endDate?.asFormattedString ?? "None Specified"}</FieldValue>
      </Field>
      {(workAgreement.isReplaced ||
        workAgreement.revisesWorkAgreement ||
        workAgreement.hasOpenRevisionRequest ||
        workAgreement.hasSourceProposal) && (
        <Field>
          <FieldTitle>History</FieldTitle>
          <WorkAgreementRevisionLinks workAgreement={workAgreement} />
        </Field>
      )}
    </WorkAgreementContainer>
  );
}
